import { isEmpty, reduce } from 'lodash';
import { PolicySigners, PolicySignersKeys } from '@ag-common-lib/lib/models/utils/policy-signers.model';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';

/**
 * Output example:
 *  {
 *    privacyPolicy: "0.0.4",
 *    termsOfService: "0.0.2"
 *  }
 */
export const lastSigningAgreements = (agreements: PolicySigners[]): Record<string, string> => {
  const result = agreements.reduce<Record<string, PolicySigners>>((acc, agreement) => {
    if (!agreement[BaseModelKeys.updatedDate]) {
      return acc;
    }
    const currentMax = acc[agreement[PolicySignersKeys.policyType]];
    if (!currentMax || new Date(agreement[BaseModelKeys.updatedDate]) > new Date(currentMax[BaseModelKeys.updatedDate]!)) {
      acc[agreement[PolicySignersKeys.policyType]] = agreement;
    }
    return acc;
  }, {});

  return Object.entries(result).reduce(
    (acc, [key, value]) => {
      acc[key] = value[PolicySignersKeys.policyVersion];
      return acc;
    },
    {} as Record<string, string | undefined>,
  );
};
/**
 * Output example:
 *  [{
 *   [BaseModelKeys.dbId]: '',
 *   [PolicySignersKeys.policyVersion]: '0.0.1'.
 *   [BaseModelKeys.updatedDate]: null,
 *   [PolicySignersKeys.policyType]: 'privacyPolicy'
 * }]
 */

export function getLastSignedAgreements(agreements: PolicySigners[]): PolicySigners[] {
  const map = new Map<string, PolicySigners>();

  agreements.forEach(agreement => {
    const currentDate = agreement[BaseModelKeys.updatedDate] ? new Date(agreement[BaseModelKeys.updatedDate]).getTime() : null;
    const existing = map.get(agreement[PolicySignersKeys.policyType]);
    const existingDate = existing && existing[BaseModelKeys.updatedDate] ? new Date(existing[BaseModelKeys.updatedDate]).getTime() : null;

    if (
      !existing ||
      (currentDate !== null && (existingDate === null || currentDate > existingDate))
    ) {
      map.set(agreement[PolicySignersKeys.policyType], agreement);
    }
  });

  return Array.from(map.values());
}

export const isVersionGreater = (a: string, b: string): boolean => {
  const aParts = a.split('.').map(Number);
  const bParts = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) {
    if (aParts[i] > bParts[i]) return true;
    if (aParts[i] < bParts[i]) return false;
  }
  return false; // versions are equal
};

/**
 * Input: ['1.2.3', '2.2.3']
 * Output example: '2.2.3'
 */
export const getMaxVersion = (versions: string[]): string | undefined => {
  if (isEmpty(versions)) {
    return undefined;
  }

  return reduce(versions, (max, current) =>
    isVersionGreater(current, max) ? current : max
  );
};
